import React, {useCallback, useRef, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import AuthHeader from '../../components/AuthHeader';
import PrimaryButton from '../../components/PrimaryButton';
import {getMyFeedback} from '../../services/feedback-service';
import {colors, card, spacing, typography} from '../../config/theme';

const PAGE_SIZE = 20;

// Backend status: pending (waiting for a reply) | replied | closed.
export const feedbackStatusStyle = status => {
  switch (String(status).toLowerCase()) {
    case 'replied':
      return {key: 'statusReplied', bg: colors.mintWash, fg: colors.brandLink};
    case 'closed':
      return {key: 'statusClosed', bg: colors.surfaceMuted, fg: colors.textMuted};
    default:
      return {
        key: 'statusPending',
        bg: colors.warningContainer,
        fg: colors.onWarningContainer,
      };
  }
};

// "FB-1001" when the server sends a ticket number, else "#<id>".
export const ticketLabel = item =>
  item?.ticketNumber || item?.ticketNo || item?.ticket || `#${item?.id}`;

export const formatFeedbackDate = iso => {
  const d = new Date(iso);
  if (!iso || isNaN(d.getTime())) {
    return '';
  }
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const StatusPill = ({status}) => {
  const {t} = useTranslation();
  const style = feedbackStatusStyle(status);
  return (
    <View style={[styles.pill, {backgroundColor: style.bg}]}>
      <Text style={[styles.pillText, {color: style.fg}]}>
        {t(`root.Feedback.${style.key}`)}
      </Text>
    </View>
  );
};

const FeedbackCard = ({item, onPress}) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    style={({pressed}) => [styles.card, pressed && styles.pressed]}>
    <View style={styles.cardTop}>
      <Text style={styles.ticket}>{ticketLabel(item)}</Text>
      <StatusPill status={item.status} />
    </View>
    <Text style={styles.subject} numberOfLines={2}>
      {item.subject}
    </Text>
    {item.message ? (
      <Text style={styles.message} numberOfLines={2}>
        {item.message}
      </Text>
    ) : null}
    <Text style={styles.date}>{formatFeedbackDate(item.createdAt)}</Text>
  </Pressable>
);

// The user's own feedback tickets (GET feedback), newest first.
const MyFeedback = ({navigation}) => {
  const {t} = useTranslation();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const loadedOnce = useRef(false);

  const load = useCallback(async (nextPage = 1) => {
    const result = await getMyFeedback({page: nextPage, limit: PAGE_SIZE});
    if (result.success) {
      setItems(prev =>
        nextPage === 1 ? result.feedback : [...prev, ...result.feedback],
      );
      setPage(nextPage);
      setHasMore(
        result.pagination?.hasNextPage ??
          result.feedback.length === PAGE_SIZE,
      );
      setLoadError(null);
    } else if (nextPage === 1) {
      setLoadError(result.error);
    }
    loadedOnce.current = true;
    setLoading(false);
    setRefreshing(false);
    setLoadingMore(false);
  }, []);

  // Reload whenever the screen is shown, so a ticket just sent appears.
  useFocusEffect(
    useCallback(() => {
      if (!loadedOnce.current) {
        setLoading(true);
      }
      load(1);
    }, [load]),
  );

  const loadMore = () => {
    if (!hasMore || loadingMore || loading) {
      return;
    }
    setLoadingMore(true);
    load(page + 1);
  };

  const renderEmpty = () => {
    if (loading) {
      return <ActivityIndicator style={styles.empty} color={colors.primary} />;
    }
    if (loadError) {
      return (
        <View style={styles.emptyBox}>
          <Text style={styles.emptyText}>{t('root.Feedback.loadFailed')}</Text>
          <PrimaryButton
            title={t('root.Feedback.retry')}
            onPress={() => {
              setLoading(true);
              load(1);
            }}
          />
        </View>
      );
    }
    return (
      <View style={styles.emptyBox}>
        <Text style={styles.emptyText}>{t('root.Feedback.empty')}</Text>
        <PrimaryButton
          title={t('root.Feedback.emptyAction')}
          onPress={() => navigation.navigate('Feedback')}
        />
      </View>
    );
  };

  return (
    <View style={styles.screen}>
      <AuthHeader
        title={t('root.Feedback.myFeedback')}
        onBack={() => navigation.goBack()}
      />
      <FlatList
        data={items}
        keyExtractor={item => String(item.id)}
        renderItem={({item}) => (
          <FeedbackCard
            item={item}
            onPress={() =>
              navigation.navigate('FeedbackDetail', {id: item.id, item})
            }
          />
        )}
        refreshing={refreshing}
        onRefresh={() => {
          setRefreshing(true);
          load(1);
        }}
        onEndReached={loadMore}
        onEndReachedThreshold={0.4}
        ListEmptyComponent={renderEmpty}
        ListFooterComponent={
          loadingMore ? (
            <ActivityIndicator style={styles.footer} color={colors.primary} />
          ) : null
        }
        contentContainerStyle={styles.list}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {flex: 1, backgroundColor: colors.background},
  list: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.md,
    paddingBottom: spacing.xxl,
    gap: spacing.md,
    flexGrow: 1,
  },
  card: {...card, padding: spacing.md, gap: spacing.xs},
  pressed: {opacity: 0.75},
  cardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  ticket: {...typography.labelMd, color: colors.textMuted},
  subject: {...typography.labelLg, letterSpacing: 0, color: colors.text},
  message: {...typography.bodyMd, color: colors.textMuted},
  date: {...typography.bodySm, color: colors.textSubtle, marginTop: 2},
  pill: {paddingHorizontal: 10, paddingVertical: 3, borderRadius: 9999},
  pillText: {...typography.labelSm},
  empty: {marginTop: spacing.xxl},
  emptyBox: {marginTop: spacing.xxl, gap: spacing.lg, alignItems: 'stretch'},
  emptyText: {...typography.bodyMd, color: colors.textMuted, textAlign: 'center'},
  footer: {marginVertical: spacing.lg},
});

export default MyFeedback;
